import { useCallback } from "react"
import type { PreOrder } from "@stockline/shared/types/orders/pre-order"
import type { PreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow"
import type { PreOrderMessage } from "@stockline/shared/types/orders/pre-order-message"
import type { PreOrderSmsMessage } from "@stockline/shared/types/orders/pre-order-sms-message"
import type { PreOrderEmail } from "@stockline/shared/types/orders/pre-order-email"
import { PRE_ORDER_TABLE } from "../../../lib/offline/resources/orders/pre-order/db"
import { PRE_ORDER_FLOW_TABLE } from "../../../lib/offline/resources/orders/pre-order-flow/db"
import { PRE_ORDER_MESSAGE_TABLE } from "../../../lib/offline/resources/orders/pre-order-message/db"
import { PRE_ORDER_SMS_MESSAGE_TABLE } from "../../../lib/offline/resources/orders/pre-order-sms-message/db"
import { PRE_ORDER_EMAIL_TABLE } from "../../../lib/offline/resources/orders/pre-order-email/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

/** Result of `mergePreOrders` — carries every ID the caller needs to (a)
 * POST to the V2 endpoint with the same UUIDs (so SSE arrives
 * idempotently) and (b) inverse-write everything on server rejection. */
export interface MergeOutcome {
  mergedId: string
  sourceIds: string[]
  clonedFlows: Array<{ sourceFlowId: string; newFlowId: string }>
  duplicatedJunctionIds: {
    whatsapp: string[]
    sms: string[]
    email: string[]
  }
}

/**
 * Offline-first batch-merge. Writes the merged PO + cloned flows +
 * duplicated junction rows + source tombstones in ONE Dexie transaction
 * so `useLiveQuery`-driven recap re-renders within one render cycle of
 * the click. Pure local write — caller is responsible for POSTing the
 * same UUIDs to /batch-merge and calling `rollbackMerge` on rejection.
 *
 * Server stays canonical: SSE returns with the same IDs and idempotently
 * overwrites local rows (server stamps `syncId`/`updatedAt`).
 */
export function usePreOrderMergeMutation() {
  const { storage } = useAISync()

  const mergePreOrders = useCallback(
    async (sourceIds: string[]): Promise<MergeOutcome> => {
      // Fail fast on an empty selection — otherwise sortedSources[0] is
      // undefined and the spread below would write a half-formed PreOrder
      // into Dexie, corrupting local state.
      if (sourceIds.length < 2) {
        throw new Error(
          `mergePreOrders requires at least 2 sources, got ${sourceIds.length}`
        )
      }
      const now = new Date().toISOString()
      const mergedId = crypto.randomUUID()
      const sources: PreOrder[] = []
      for (const id of sourceIds) {
        const s = await storage.get<PreOrder>(PRE_ORDER_TABLE, id)
        if (!s) throw new Error(`Merge source ${id} not in offline store`)
        sources.push(s)
      }

      // createdAt ASC — same order the server uses, so the header copy
      // matches what SSE will reconcile against in a moment.
      const sortedSources = [...sources].sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt)
      )
      const oldest = sortedSources[0]

      // Mirror server's header-copy + junction pointer pick logic. SSE
      // overwrites the merged row with server-stamped fields (updatedAt,
      // syncId); anything imperfect here flickers briefly then snaps to
      // canonical when the SSE event arrives.
      const mergedRow: PreOrder = {
        ...oldest,
        id: mergedId,
        preOrderNumber: null,
        customerPoNumber: joinDistinct(
          sortedSources.map((s) => s.customerPoNumber)
        ),
        comment: joinDistinct(sortedSources.map((s) => s.comment)),
        attachedFiles: {
          files: sortedSources.flatMap((s) => s.attachedFiles?.files ?? []),
        },
        whatsappChatId:
          sortedSources.find((s) => s.whatsappChatId)?.whatsappChatId ?? null,
        emailId: sortedSources.find((s) => s.emailId)?.emailId ?? null,
        smsChatId: sortedSources.find((s) => s.smsChatId)?.smsChatId ?? null,
        syncId: 0,
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      }

      // Pre-compute new flow UUIDs per source flow so the eventual server
      // payload can pass them through and SSE returns idempotently. Per
      // sortedSources order so flowIndex assignment matches the server's
      // [source-A flows..., source-B flows..., ...] renumbering.
      const allSourceFlows: PreOrderFlow[] = []
      for (const src of sortedSources) {
        const flows = await storage.getAll<PreOrderFlow>(PRE_ORDER_FLOW_TABLE, {
          preOrderId: src.id,
        })
        const active = flows
          .filter((f) => !f.deletedAt)
          .sort((a, b) => {
            // Same ordering as server's ROW_NUMBER OVER (ORDER BY "index"
            // NULLS LAST, created_at, id) — keeps within-source order
            // identical so flowIndex 0..N matches.
            const ai = a.flowIndex ?? Number.MAX_SAFE_INTEGER
            const bi = b.flowIndex ?? Number.MAX_SAFE_INTEGER
            if (ai !== bi) return ai - bi
            const ac = a.createdAt.localeCompare(b.createdAt)
            if (ac !== 0) return ac
            return a.id.localeCompare(b.id)
          })
        allSourceFlows.push(...active)
      }
      const clonedFlows = allSourceFlows.map((f) => ({
        sourceFlowId: f.id,
        newFlowId: crypto.randomUUID(),
        source: f,
      }))

      // Pre-compute junction duplicates with deterministic UUIDs so
      // rollback can delete them by id without re-querying.
      const whatsappSrc: PreOrderMessage[] = []
      const smsSrc: PreOrderSmsMessage[] = []
      const emailSrc: PreOrderEmail[] = []
      for (const id of sourceIds) {
        whatsappSrc.push(
          ...(await storage.getAll<PreOrderMessage>(PRE_ORDER_MESSAGE_TABLE, {
            preOrderId: id,
          }))
        )
        smsSrc.push(
          ...(await storage.getAll<PreOrderSmsMessage>(
            PRE_ORDER_SMS_MESSAGE_TABLE,
            { preOrderId: id }
          ))
        )
        emailSrc.push(
          ...(await storage.getAll<PreOrderEmail>(PRE_ORDER_EMAIL_TABLE, {
            preOrderId: id,
          }))
        )
      }
      const whatsappDupes: PreOrderMessage[] = whatsappSrc
        .filter((j) => !j.deletedAt)
        .map((j) => ({ ...j, id: crypto.randomUUID(), preOrderId: mergedId }))
      const smsDupes: PreOrderSmsMessage[] = smsSrc
        .filter((j) => !j.deletedAt)
        .map((j) => ({ ...j, id: crypto.randomUUID(), preOrderId: mergedId }))
      const emailDupes: PreOrderEmail[] = emailSrc
        .filter((j) => !j.deletedAt)
        .map((j) => ({ ...j, id: crypto.randomUUID(), preOrderId: mergedId }))

      await storage.transaction(
        [
          PRE_ORDER_TABLE,
          PRE_ORDER_FLOW_TABLE,
          PRE_ORDER_MESSAGE_TABLE,
          PRE_ORDER_SMS_MESSAGE_TABLE,
          PRE_ORDER_EMAIL_TABLE,
        ],
        async () => {
          // 1. Insert merged PO.
          await storage.put(PRE_ORDER_TABLE, mergedRow)

          // 2. Clone source flows under the merged PO with renumbered
          //    flowIndex matching the server's contiguous 0..N-1.
          const newFlows: PreOrderFlow[] = clonedFlows.map(
            ({ newFlowId, source }, idx) => ({
              ...source,
              id: newFlowId,
              preOrderId: mergedId,
              flowIndex: idx,
              syncId: 0,
              createdAt: now,
              updatedAt: now,
              deletedAt: null,
            })
          )
          if (newFlows.length > 0) {
            await storage.putMany(PRE_ORDER_FLOW_TABLE, newFlows)
          }

          // 3. Duplicate junction rows so the merged PO shares the comms
          //    history. Server does the same in the same transaction.
          if (whatsappDupes.length > 0) {
            await storage.putMany(PRE_ORDER_MESSAGE_TABLE, whatsappDupes)
          }
          if (smsDupes.length > 0) {
            await storage.putMany(PRE_ORDER_SMS_MESSAGE_TABLE, smsDupes)
          }
          if (emailDupes.length > 0) {
            await storage.putMany(PRE_ORDER_EMAIL_TABLE, emailDupes)
          }

          // 4. Tombstone source POs.
          const tombstonedSources = sources.map((s) => ({
            ...s,
            deletedAt: now,
            updatedAt: now,
          }))
          await storage.putMany(PRE_ORDER_TABLE, tombstonedSources)

          // 5. Tombstone source flows.
          const tombstonedFlows = allSourceFlows.map((f) => ({
            ...f,
            deletedAt: now,
            updatedAt: now,
          }))
          if (tombstonedFlows.length > 0) {
            await storage.putMany(PRE_ORDER_FLOW_TABLE, tombstonedFlows)
          }
        }
      )

      return {
        mergedId,
        sourceIds,
        clonedFlows: clonedFlows.map(({ sourceFlowId, newFlowId }) => ({
          sourceFlowId,
          newFlowId,
        })),
        duplicatedJunctionIds: {
          whatsapp: whatsappDupes.map((j) => j.id),
          sms: smsDupes.map((j) => j.id),
          email: emailDupes.map((j) => j.id),
        },
      }
    },
    [storage]
  )

  const rollbackMerge = useCallback(
    async (outcome: MergeOutcome): Promise<void> => {
      const now = new Date().toISOString()
      await storage.transaction(
        [
          PRE_ORDER_TABLE,
          PRE_ORDER_FLOW_TABLE,
          PRE_ORDER_MESSAGE_TABLE,
          PRE_ORDER_SMS_MESSAGE_TABLE,
          PRE_ORDER_EMAIL_TABLE,
        ],
        async () => {
          // 1. Drop merged PO.
          await storage.delete(PRE_ORDER_TABLE, outcome.mergedId)

          // 2. Drop cloned flows.
          for (const { newFlowId } of outcome.clonedFlows) {
            await storage.delete(PRE_ORDER_FLOW_TABLE, newFlowId)
          }

          // 3. Drop duplicated junctions.
          for (const id of outcome.duplicatedJunctionIds.whatsapp) {
            await storage.delete(PRE_ORDER_MESSAGE_TABLE, id)
          }
          for (const id of outcome.duplicatedJunctionIds.sms) {
            await storage.delete(PRE_ORDER_SMS_MESSAGE_TABLE, id)
          }
          for (const id of outcome.duplicatedJunctionIds.email) {
            await storage.delete(PRE_ORDER_EMAIL_TABLE, id)
          }

          // 4. Un-tombstone source POs.
          for (const id of outcome.sourceIds) {
            const existing = await storage.get<PreOrder>(PRE_ORDER_TABLE, id)
            if (existing) {
              await storage.put(PRE_ORDER_TABLE, {
                ...existing,
                deletedAt: null,
                updatedAt: now,
              })
            }
          }

          // 5. Un-tombstone source flows. SSE replay from the canonical
          //    server state will overwrite syncId/updatedAt on next sync.
          for (const { sourceFlowId } of outcome.clonedFlows) {
            const f = await storage.get<PreOrderFlow>(
              PRE_ORDER_FLOW_TABLE,
              sourceFlowId
            )
            if (f) {
              await storage.put(PRE_ORDER_FLOW_TABLE, {
                ...f,
                deletedAt: null,
                updatedAt: now,
              })
            }
          }
        }
      )
    },
    [storage]
  )

  return { mergePreOrders, rollbackMerge }
}

function joinDistinct(values: Array<string | null>): string | null {
  const seen = new Set<string>()
  for (const v of values) {
    const trimmed = v?.trim()
    if (trimmed) seen.add(trimmed)
  }
  if (seen.size === 0) return null
  return Array.from(seen).join(" / ")
}
